import { AVAILABLE_TASKS_TYPES } from 'volta/models/constants/widgets';

import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';

import type {
  IDashboardWidgetWithComponent,
  IWidgetNames,
  TDashboardColumn
} from 'volta/models/types/user';
import type { TAssignTo, TTaskType } from 'volta/models/types/tasks';
import type IntlService from 'ember-intl/services/intl';
import type { IBvMenuItem, IBvMenuSection } from 'volta/components/bv-menu';
interface IArgs {
  widget: IDashboardWidgetWithComponent;
  onChange?: (widget: IDashboardWidgetWithComponent, column: TDashboardColumn) => void;
  column: TDashboardColumn;
  currentDisplay: TDashboardColumn;
}

export default class WidgetEditor extends Component<IArgs> {
  @service intl!: IntlService;

  get borderColor(): string {
    return this.args.widget.columnsDisplay.includes(
      this.args.column === 'primary' ? 'secondary' : 'primary'
    ) && this.args.currentDisplay === 'secondary'
      ? 'var(--bv-color-border-success)'
      : 'var(--bv-color-border-light)';
  }

  get hasArgs() {
    const widgetNamesWithArgs: IWidgetNames[] = ['tableauReport', 'planningKpis', 'planningReport'];
    return widgetNamesWithArgs.includes(this.args.widget.name);
  }

  get menu(): IBvMenuSection[] {
    const { name, args } = this.args.widget;

    if (name === 'planningReport' || name === 'planningKpis' || name === 'planningOrders') {
      const disablingArgsOnly = Object.entries(args as object).filter(([item, options]) => {
        return (
          options &&
          typeof options === 'object' &&
          'disabled' in options &&
          this.intl.exists(`dashboardPage.widgetOptions.${item}`)
        );
      });

      const items = disablingArgsOnly.map(([item, options]): IBvMenuItem => {
        return {
          selected: !options.disabled,
          content: this.intl.t(`dashboardPage.widgetOptions.${item}`),
          onClick: () => this.onOptionsToggleActivation(item),
          disabled: false
        };
      });
      return [
        {
          title: this.intl.t(`dashboardPage.menu`),
          items
        }
      ];
    }
    if (name === 'dueTasks') {
      const assignTo = AVAILABLE_TASKS_TYPES.assignTo.map(
        (option: TAssignTo): IBvMenuItem => ({
          selected: args?.assignTo === option,
          content: this.intl.t(`tasksPage.assignTo.${option}`),
          onClick: () => this.onTasksAssignToActivation(option),
          disabled: false
        })
      );

      const tasksTypes = AVAILABLE_TASKS_TYPES.types.map(
        (option: Partial<TTaskType>): IBvMenuItem => ({
          selected: (args?.types ?? []).includes(option),
          content: this.intl.t(`tasksPage.types.${option}`),
          onClick: () => this.onTasksTypeToggleActivation(option),
          disabled: false
        })
      );
      return [
        {
          title: this.intl.t(`dashboardPage.assignTo`),
          items: assignTo
        },
        {
          title: this.intl.t(`dashboardPage.tasksTypes`),
          items: tasksTypes
        }
      ];
    }
    return [];
  }

  @action
  onToggleActivation(_disabled: boolean): void {
    const { widget, column } = this.args;

    widget.disabled = !widget.disabled;

    this.args.onChange?.({ ...widget }, column);
  }

  @action
  onOptionsToggleActivation(option: string): void {
    const { widget, column } = this.args;

    if (isPresent(widget.args?.[option]?.disabled)) {
      widget.args![option].disabled = !widget.args![option].disabled;
    }

    this.args.onChange?.({ ...widget }, column);
  }

  @action
  onTasksAssignToActivation(option: TAssignTo): void {
    const { widget, column } = this.args;
    if (!widget.args) {
      throw new Error('Task Widget must have args.');
    }
    if (widget.args.assignTo === option) {
      widget.args.assignTo = undefined;
    } else {
      widget.args.assignTo = option;
    }
    this.args.onChange?.({ ...widget }, column);
  }

  @action
  onTasksTypeToggleActivation(option: Partial<TTaskType>): void {
    const { widget, column } = this.args;
    if (!widget.args) {
      throw new Error('Task Widget must have args.');
    }
    const types = new Set(widget.args.types ?? []);
    if (types && types.has(option)) {
      types.delete(option);
      widget.args.types = [...types];
    } else if (types) {
      types.add(option);
      widget.args.types = [...types];
    }

    this.args.onChange?.({ ...widget }, column);
  }

  @action
  onEdition(widget: IDashboardWidgetWithComponent) {
    const { column } = this.args;

    this.args.onChange?.({ ...widget }, column);
  }

  itemSections = (...sections: IBvMenuSection[]) => {
    return sections.flat();
  };
}
